import React, { useState } from "react";
import DOMPurify from "dompurify";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import Tooltip from "@mui/material/Tooltip";
import {
  Typography,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CodeIcon from "@mui/icons-material/Code";
import PersonIcon from "@mui/icons-material/Person";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  detectLanguage,
  hasHTMLContent,
  getHTMLContent,
  getDisplayText,
} from "./chatUtils";

// Safe HTML renderer component (DOMPurify + accordion)
export const HTMLRenderer = ({ htmlContent }) => {
  const [isExpanded, setIsExpanded] = useState(true); // Open by default

  // Sanitize HTML content to prevent XSS attacks
  const sanitizedHTML = htmlContent
    ? DOMPurify.sanitize(htmlContent, {
        ALLOWED_TAGS: ["p", "div", "span", "br", "strong", "em", "u", "b", "i", "h1", "h2", "h3", "h4", "h5", "h6", "ul", "ol", "li", "a", "img", "pre", "code", "blockquote", "table", "thead", "tbody", "tr", "td", "th"],
        ALLOWED_ATTR: ["style", "href", "src", "alt", "title", "class", "id"],
        ALLOW_DATA_ATTR: false,
        ALLOW_UNKNOWN_PROTOCOLS: false,
      })
    : "";

  return (
    <Box sx={{ marginTop: 2 }}>
      <Accordion
        expanded={isExpanded}
        onChange={() => setIsExpanded(!isExpanded)}
        sx={{
          backgroundColor: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: "8px",
          boxShadow: "none",
          "&:before": { display: "none" },
          "& .MuiAccordionSummary-root": {
            minHeight: "auto",
            padding: "8px 16px",
            "&.Mui-expanded": {
              minHeight: "auto",
            },
          },
          "& .MuiAccordionDetails-root": {
            padding: "0 16px 16px",
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "#667eea" }} />}
          sx={{
            "& .MuiAccordionSummary-content": {
              alignItems: "center",
              gap: 1,
            },
          }}
        >
          <CodeIcon sx={{ fontSize: "1rem", color: "#667eea" }} />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: "#667eea",
              fontSize: "0.8rem",
            }}
          >
            Visualisation
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Card
            variant="outlined"
            sx={{
              backgroundColor: "white",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            <CardContent sx={{ padding: "16px !important" }}>
              <Box
                dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
                sx={{
                  "& *": {
                    maxWidth: "100%",
                    wordWrap: "break-word",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                  },
                  "& img": {
                    maxWidth: "100%",
                    height: "auto",
                  },
                  "& pre": {
                    backgroundColor: "#f1f5f9",
                    padding: "12px",
                    borderRadius: "6px",
                    overflow: "auto",
                    fontSize: "0.85rem",
                  },
                  "& code": {
                    backgroundColor: "#f1f5f9",
                    padding: "2px 6px",
                    borderRadius: "4px",
                    fontSize: "0.85rem",
                  },
                }}
              />
            </CardContent>
          </Card>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

// Markdown renderer for AI messages, styled to match the MUI bubble look.
const MarkdownContent = ({ text }) => (
  <Box
    sx={{
      fontSize: "0.88rem",
      fontWeight: 400,
      lineHeight: 1.65,
      wordBreak: "break-word",
      overflowWrap: "anywhere",
      fontFamily: (theme) => theme.typography.body2.fontFamily,
      "& p": { margin: 0, marginBottom: "8px", whiteSpace: "pre-wrap" },
      "& p:last-child": { marginBottom: 0 },
      "& strong": { fontWeight: 600 },
      "& em": { fontStyle: "italic", opacity: 0.88 },
      "& ul, & ol": { margin: "4px 0 8px", paddingInlineStart: "22px" },
      "& li": { marginBottom: "3px" },
      "& li:last-child": { marginBottom: 0 },
      "& a": {
        color: "#6366f1",
        fontWeight: 500,
        textDecorationColor: "rgba(99,102,241,0.4)",
        "&:hover": { color: "#4f46e5" },
      },
      "& h1, & h2, & h3, & h4, & h5, & h6": {
        margin: "10px 0 6px",
        lineHeight: 1.35,
        fontWeight: 700,
        color: "inherit",
      },
      "& h1": { fontSize: "1.05rem" },
      "& h2": { fontSize: "1rem" },
      "& h3, & h4, & h5, & h6": { fontSize: "0.92rem" },
      "& code": {
        backgroundColor: "#f1f5f9",
        border: "1px solid #e2e8f0",
        padding: "1px 6px",
        borderRadius: "4px",
        fontSize: "0.82rem",
        fontFamily: "'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace",
      },
      "& pre": {
        backgroundColor: "#f1f5f9",
        border: "1px solid #e2e8f0",
        padding: "12px",
        borderRadius: "8px",
        overflow: "auto",
        margin: "6px 0 10px",
        "& code": {
          backgroundColor: "transparent",
          border: "none",
          padding: 0,
          fontSize: "0.82rem",
        },
      },
      "& blockquote": {
        borderLeft: "3px solid #c7d2fe",
        margin: "6px 0 10px",
        padding: "2px 12px",
        color: "#475569",
      },
      "& table": {
        borderCollapse: "collapse",
        margin: "6px 0 10px",
        maxWidth: "100%",
        display: "block",
        overflowX: "auto",
        fontSize: "0.82rem",
      },
      "& th, & td": {
        border: "1px solid #e2e8f0",
        padding: "5px 10px",
        textAlign: "left",
      },
      "& th": { backgroundColor: "#f8fafc", fontWeight: 600 },
      "& hr": { border: "none", borderTop: "1px solid #e2e8f0", margin: "10px 0" },
    }}
  >
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        // Links always open in a new tab, safely.
        a: ({ node, ...props }) => (
          <a {...props} target="_blank" rel="noopener noreferrer">
            {props.children}
          </a>
        ),
      }}
    >
      {text || ""}
    </ReactMarkdown>
  </Box>
);

// One chat message: avatar, sender label, bubble (markdown / HTML / actions),
// per-message actions (copy on AI messages, retry on failed turns).
const MessageBubble = ({
  msg,
  pendingRecommendationSpaces,
  onRecommendationResponse,
  onGuidanceResponse,
  onRetry,
}) => {
  const [copied, setCopied] = useState(false);
  const isUser = msg.user === "You";
  const isSystem = msg.user === "system";
  const showHTML = isSystem && hasHTMLContent(msg);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(msg.text || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      console.error("Failed to copy message:", error);
    }
  };

  return (
    <Box
      sx={{
        margin: isUser ? "10px 0 10px 40px" : "10px 40px 10px 0",
        display: "flex",
        flexDirection: isUser ? "row-reverse" : "row",
        alignItems: "flex-end",
        gap: 1.5,
        "&:hover .message-copy-btn": { opacity: 1 },
      }}
    >
      {/* Avatar */}
      <Avatar
        sx={{
          width: 28,
          height: 28,
          flexShrink: 0,
          background: isUser
            ? "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)"
            : "#f1f5f9",
          border: isUser ? "none" : "1.5px solid #e2e8f0",
          boxShadow: isUser ? "0 2px 8px rgba(99,102,241,0.35)" : "none",
        }}
      >
        {isUser ? (
          <PersonIcon sx={{ fontSize: "0.95rem", color: "white" }} />
        ) : (
          <AutoAwesomeIcon sx={{ fontSize: "0.95rem", color: "#6366f1" }} />
        )}
      </Avatar>

      {/* Message Content */}
      <Box
        sx={{
          maxWidth: "min(78%, 760px)",
          display: "flex",
          flexDirection: "column",
          alignItems: isUser ? "flex-end" : "flex-start",
        }}
      >
        {/* Sender label */}
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.68rem",
            fontWeight: 600,
            color: "#94a3b8",
            marginBottom: "5px",
            paddingX: "4px",
            letterSpacing: "0.03em",
            textTransform: "uppercase",
          }}
        >
          {isUser ? "You" : "AI Agent"}
        </Typography>

        {/* Bubble + copy action */}
        <Box sx={{ position: "relative", maxWidth: "100%" }}>
          {/* Message Bubble */}
          <Box
            sx={{
              padding: "11px 15px",
              borderRadius: isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
              background: isUser
                ? "linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)"
                : "#f8fafc",
              color: isUser ? "white" : "#1e293b",
              boxShadow: isUser
                ? "0 4px 16px rgba(99,102,241,0.3)"
                : "0 1px 4px rgba(0,0,0,0.06)",
              border: isUser
                ? "none"
                : msg.isError
                ? "1px solid #fecaca"
                : "1px solid #e2e8f0",
              wordWrap: "break-word",
              overflowWrap: "anywhere",
              overflow: "hidden",
              lineHeight: 1.5,
              transition: "box-shadow 0.2s ease",
            }}
          >
            {isSystem ? (
              // AI message text renders as markdown
              <MarkdownContent text={showHTML ? getDisplayText(msg) : msg.text} />
            ) : (
              // User messages stay plain text
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.88rem",
                  fontWeight: 400,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                  lineHeight: 1.65,
                }}
              >
                {msg.text}
              </Typography>
            )}

            {/* Render HTML content if present */}
            {showHTML && <HTMLRenderer htmlContent={getHTMLContent(msg)} />}

            {/* Retry button on failed turns */}
            {isSystem && msg.isError && msg.failedCommand && onRetry && (
              <Box sx={{ marginTop: 1.5 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ReplayIcon sx={{ fontSize: "0.9rem" }} />}
                  onClick={() => onRetry(msg)}
                  sx={{
                    borderColor: "#c7d2fe",
                    color: "#4f46e5",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    padding: "3px 14px",
                    borderRadius: "20px",
                    textTransform: "none",
                    "&:hover": {
                      borderColor: "#6366f1",
                      backgroundColor: "#eef2ff",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  Retry
                </Button>
              </Box>
            )}

            {/* Recommendation prompt buttons */}
            {isSystem &&
              msg.isRecommendationPrompt &&
              pendingRecommendationSpaces &&
              (() => {
                const messageLanguage = detectLanguage(msg.text);

                return (
                  <Box
                    sx={{
                      marginTop: 2,
                      display: "flex",
                      gap: 1,
                      justifyContent: "flex-start",
                    }}
                  >
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => onRecommendationResponse(true)}
                      sx={{
                        background: "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)",
                        color: "white",
                        fontSize: "0.8rem",
                        padding: "6px 16px",
                        borderRadius: "20px",
                        textTransform: "none",
                        fontWeight: 600,
                        "&:hover": {
                          background: "linear-gradient(135deg, #45a049 0%, #3d8b40 100%)",
                          transform: "translateY(-1px)",
                          boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
                        },
                        transition: "all 0.2s ease",
                      }}
                    >
                      {messageLanguage === "hebrew" ? "✅ כן, הפעל המלצות" : "✅ Yes, enable recommendations"}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => onRecommendationResponse(false)}
                      sx={{
                        borderColor: "#e0e0e0",
                        color: "#666",
                        fontSize: "0.8rem",
                        padding: "6px 16px",
                        borderRadius: "20px",
                        textTransform: "none",
                        fontWeight: 600,
                        "&:hover": {
                          borderColor: "#ccc",
                          backgroundColor: "#f5f5f5",
                          transform: "translateY(-1px)",
                        },
                        transition: "all 0.2s ease",
                      }}
                    >
                      {messageLanguage === "hebrew" ? "❌ לא, תודה" : "❌ No, thanks"}
                    </Button>
                  </Box>
                );
              })()}

            {/* Guidance prompt buttons */}
            {isSystem &&
              msg.isGuidancePrompt &&
              (() => {
                const messageLanguage = detectLanguage(msg.text);

                return (
                  <Box
                    sx={{
                      marginTop: 2,
                      display: "flex",
                      gap: 1,
                      justifyContent: "flex-start",
                    }}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => onGuidanceResponse(false)}
                      sx={{
                        borderColor: "#e0e0e0",
                        color: "#666",
                        fontSize: "0.8rem",
                        padding: "6px 16px",
                        borderRadius: "20px",
                        textTransform: "none",
                        fontWeight: 600,
                        "&:hover": {
                          borderColor: "#ccc",
                          backgroundColor: "#f5f5f5",
                          transform: "translateY(-1px)",
                        },
                        transition: "all 0.2s ease",
                      }}
                    >
                      {messageLanguage === "hebrew" ? "⏭️ לא, המשך בלי הכוונה" : "⏭️ No, continue without guidance"}
                    </Button>
                  </Box>
                );
              })()}

            {/* Timestamp */}
            {msg.date && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.7rem",
                  opacity: 0.7,
                  marginTop: "4px",
                  display: "block",
                  textAlign: isUser ? "right" : "left",
                }}
              >
                {new Date(msg.date).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </Typography>
            )}
          </Box>

          {/* Copy-to-clipboard on AI messages (visible on hover) */}
          {isSystem && (msg.text || "").trim() && (
            <Tooltip title={copied ? "Copied!" : "Copy message"} placement="top">
              <IconButton
                className="message-copy-btn"
                size="small"
                aria-label="Copy message"
                onClick={handleCopy}
                sx={{
                  position: "absolute",
                  top: 2,
                  right: -34,
                  opacity: copied ? 1 : 0,
                  transition: "opacity 0.15s ease",
                  color: copied ? "#22c55e" : "#94a3b8",
                  backgroundColor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  padding: "4px",
                  borderRadius: "8px",
                  "&:hover": {
                    color: copied ? "#22c55e" : "#6366f1",
                    backgroundColor: "#f8fafc",
                  },
                }}
              >
                {copied ? (
                  <CheckIcon sx={{ fontSize: "0.85rem" }} />
                ) : (
                  <ContentCopyIcon sx={{ fontSize: "0.85rem" }} />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MessageBubble;
